import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Switch,
    FormControlLabel,
    Divider,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { FaSave, FaCog } from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { getSystemSettings, updateSystemSettings } from '../../services/api';

const Container = styled(Box)({
    padding: '2rem',
});

const PageTitle = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    color: AppColors.primaryDark,
    marginBottom: '2rem',
});

const SettingsCard = styled(Paper)({
    padding: '2rem',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '2rem',
});

const Settings = () => {
    const [settings, setSettings] = useState({
        platform_fee: 5,
        min_offer_amount: 50,
        max_offer_amount: 50000,
        auto_approve_vendors: false,
        email_notifications: true,
        sms_notifications: false,
        maintenance_mode: false,
        api_rate_limit: 100,
        session_timeout: 30,
        max_image_size: 5,
        allowed_file_types: 'jpg,jpeg,png,pdf',
        backup_frequency: 24,
        log_retention_days: 30
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await getSystemSettings();
            setSettings({ ...settings, ...response.settings });
        } catch (err) {
            setError('Failed to fetch settings');
            console.error('Error fetching settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);
        
        try {
            await updateSystemSettings(settings);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (loading) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <PageTitle>System Settings</PageTitle>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                    Settings saved successfully!
                </Alert>
            )}

            {/* Business Settings */}
            <SettingsCard>
                <Box display="flex" alignItems="center" mb={3}>
                    <FaCog color={AppColors.primaryDark} size={20} />
                    <Typography variant="h6" sx={{ ml: 1 }}>
                        Business Settings
                    </Typography>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Platform Fee (%)"
                            type="number"
                            value={settings.platform_fee}
                            onChange={(e) => handleChange('platform_fee', parseFloat(e.target.value))}
                            inputProps={{ min: 0, max: 50, step: 0.1 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Minimum Offer Amount (AED)"
                            type="number"
                            value={settings.min_offer_amount}
                            onChange={(e) => handleChange('min_offer_amount', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Maximum Offer Amount (AED)"
                            type="number"
                            value={settings.max_offer_amount}
                            onChange={(e) => handleChange('max_offer_amount', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.auto_approve_vendors}
                                    onChange={(e) => handleChange('auto_approve_vendors', e.target.checked)}
                                />
                            }
                            label="Auto-approve New Vendors"
                        />
                    </Grid>
                </Grid>
            </SettingsCard>

            {/* Notification Settings */}
            <SettingsCard>
                <Typography variant="h6" gutterBottom>
                    Notification Settings
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.email_notifications}
                                    onChange={(e) => handleChange('email_notifications', e.target.checked)}
                                />
                            }
                            label="Email Notifications"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.sms_notifications}
                                    onChange={(e) => handleChange('sms_notifications', e.target.checked)}
                                />
                            }
                            label="SMS Notifications"
                        />
                    </Grid>
                </Grid>
            </SettingsCard>

            {/* System Settings */}
            <SettingsCard>
                <Typography variant="h6" gutterBottom>
                    System Configuration
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="API Rate Limit (requests/minute)"
                            type="number"
                            value={settings.api_rate_limit}
                            onChange={(e) => handleChange('api_rate_limit', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Session Timeout (minutes)"
                            type="number"
                            value={settings.session_timeout}
                            onChange={(e) => handleChange('session_timeout', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Max Image Size (MB)"
                            type="number"
                            value={settings.max_image_size}
                            onChange={(e) => handleChange('max_image_size', parseFloat(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Allowed File Types"
                            value={settings.allowed_file_types}
                            onChange={(e) => handleChange('allowed_file_types', e.target.value)}
                            helperText="Comma-separated list (e.g., jpg,png,pdf)"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Backup Frequency (hours)"
                            type="number"
                            value={settings.backup_frequency}
                            onChange={(e) => handleChange('backup_frequency', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Log Retention (days)"
                            type="number"
                            value={settings.log_retention_days}
                            onChange={(e) => handleChange('log_retention_days', parseInt(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.maintenance_mode}
                                    onChange={(e) => handleChange('maintenance_mode', e.target.checked)}
                                />
                            }
                            label="Maintenance Mode"
                            sx={{ color: settings.maintenance_mode ? AppColors.warningOrange : 'inherit' }}
                        />
                    </Grid>
                </Grid>
            </SettingsCard>

            {/* Save Button */}
            <Box display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} /> : <FaSave />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        backgroundColor: AppColors.primaryDark,
                        '&:hover': { backgroundColor: AppColors.primaryYellow, color: AppColors.primaryDark },
                        px: 4,
                        py: 1.5
                    }}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </Box>
        </Container>
    );
};

export default Settings;
